import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DashboardFilters, DashboardFiltersBar } from "../../components/DashboardFiltersBar";
import { useAuth } from "../../auth/AuthContext";
import { getCurrentMonthRange } from "../../utils/dateUtils";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type PaymentsDashboardData = {
  totalPaid: {
    amount: number;
    interest: number;
    fine: number;
    discount: number;
    count: number;
  };
  totalReceived: {
    amount: number;
    interest: number;
    fine: number;
    discount: number;
    count: number;
  };
  paymentsByMethod: Array<{
    paymentMethod: string;
    type: string;
    total: number;
    count: number;
  }>;
  dailySeries: Array<{
    date: string;
    paid: number;
    received: number;
  }>;
  topPayments: Array<{
    id: number;
    date: string;
    description: string;
    supplier: string;
    amount: number;
    paymentMethod: string;
  }>;
  topReceivables: Array<{
    id: number;
    date: string;
    description: string;
    client: string;
    amount: number;
    paymentMethod: string;
  }>;
};

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#8dd1e1",
];

export const PaymentsDashboardPage: React.FC = () => {
  const { client } = useAuth();
  const [filters, setFilters] = useState<DashboardFilters>(() => getCurrentMonthRange());
  const [data, setData] = useState<PaymentsDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
    return params.toString();
  }, [filters]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const response = await client.get(`/dash/payments?${query}`);
      if (!response.ok) {
        setData(null);
        setError("Falha ao carregar dashboard de pagamentos.");
        setLoading(false);
        return;
      }
      setData((await response.json()) as PaymentsDashboardData);
      setLoading(false);
    };
    fetchData();
  }, [client, query]);

  const totalPaid = data?.totalPaid;
  const totalReceived = data?.totalReceived;
  const paymentsByMethod = data?.paymentsByMethod ?? [];
  const dailySeries = data?.dailySeries ?? [];
  const topPayments = data?.topPayments ?? [];
  const topReceivables = data?.topReceivables ?? [];

  const netBalance = (totalReceived?.amount || 0) - (totalPaid?.amount || 0);

  return (
    <div className="container-fluid py-4">
      <h1 className="mb-3">Dashboard de Pagamentos e Recebimentos</h1>
      <DashboardFiltersBar value={filters} onChange={setFilters} />
      {loading && <div className="alert alert-info">Carregando...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card shadow-sm h-100 border-danger">
            <div className="card-body">
              <h6 className="text-muted">Total Pago</h6>
              <h3 className="text-danger">{formatMoneyBR(totalPaid?.amount ?? 0)}</h3>
              <div className="mt-2">
                <small className="text-muted d-block">
                  {totalPaid?.count || 0} pagamento{totalPaid?.count !== 1 ? "s" : ""}
                </small>
                {totalPaid && (totalPaid.interest > 0 || totalPaid.fine > 0) && (
                  <small className="text-danger d-block">
                    + {formatMoneyBR(totalPaid.interest + totalPaid.fine)} (juros/multa)
                  </small>
                )}
                {totalPaid && totalPaid.discount > 0 && (
                  <small className="text-success d-block">
                    - {formatMoneyBR(totalPaid.discount)} (desconto)
                  </small>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm h-100 border-success">
            <div className="card-body">
              <h6 className="text-muted">Total Recebido</h6>
              <h3 className="text-success">
                {formatMoneyBR(totalReceived?.amount ?? 0)}
              </h3>
              <div className="mt-2">
                <small className="text-muted d-block">
                  {totalReceived?.count || 0} recebimento
                  {totalReceived?.count !== 1 ? "s" : ""}
                </small>
                {totalReceived && (totalReceived.interest > 0 || totalReceived.fine > 0) && (
                  <small className="text-success d-block">
                    + {formatMoneyBR(totalReceived.interest + totalReceived.fine)} (juros/multa)
                  </small>
                )}
                {totalReceived && totalReceived.discount > 0 && (
                  <small className="text-danger d-block">
                    - {formatMoneyBR(totalReceived.discount)} (desconto)
                  </small>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm h-100 border-primary">
            <div className="card-body">
              <h6 className="text-muted">Saldo Liquido</h6>
              <h3 className={netBalance >= 0 ? "text-success" : "text-danger"}>
                {formatMoneyBR(Math.abs(netBalance))}
              </h3>
              <div className="mt-2">
                <small className="text-muted d-block">
                  {netBalance >= 0 ? "Superavit" : "Deficit"} no periodo
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-8">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h6 className="text-muted mb-3">Pagamentos e Recebimentos por Dia</h6>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailySeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(date) => formatDateBR(date)} />
                  <YAxis tickFormatter={(value) => formatMoneyBR(value)} />
                  <Tooltip
                    formatter={(value: number) => formatMoneyBR(value)}
                    labelFormatter={(label) => formatDateBR(label)}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="paid" stroke="#dc3545" name="Pago" strokeWidth={2} />
                  <Line type="monotone" dataKey="received" stroke="#28a745" name="Recebido" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h6 className="text-muted mb-3">Por Forma de Pagamento</h6>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={paymentsByMethod}
                    dataKey="total"
                    nameKey="paymentMethod"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry) =>
                      `${entry.paymentMethod}: ${formatMoneyBR(entry.total)}`
                    }
                  >
                    {paymentsByMethod.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatMoneyBR(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-3">Detalhamento por Forma de Pagamento</h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Forma de Pagamento</th>
                      <th>Tipo</th>
                      <th className="text-end">Quantidade</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentsByMethod.map((method, index) => (
                      <tr key={index}>
                        <td>{method.paymentMethod}</td>
                        <td>
                          <span className="badge bg-secondary">{method.type}</span>
                        </td>
                        <td className="text-end">{method.count}</td>
                        <td className="text-end">{formatMoneyBR(method.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-3">Top 5 Maiores Pagamentos</h6>
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descricao</th>
                      <th>Fornecedor</th>
                      <th className="text-end">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td>{formatDateBR(payment.date)}</td>
                        <td>{payment.description}</td>
                        <td>
                          <small className="text-muted">{payment.supplier}</small>
                        </td>
                        <td className="text-end text-danger fw-semibold">
                          {formatMoneyBR(payment.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-3">Top 5 Maiores Recebimentos</h6>
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descricao</th>
                      <th>Cliente</th>
                      <th className="text-end">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topReceivables.map((receivable) => (
                      <tr key={receivable.id}>
                        <td>{formatDateBR(receivable.date)}</td>
                        <td>{receivable.description}</td>
                        <td>
                          <small className="text-muted">{receivable.client}</small>
                        </td>
                        <td className="text-end text-success fw-semibold">
                          {formatMoneyBR(receivable.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
